import { createContext, type FC, type ReactNode, useContext } from "react";
import type { AnalysisTarget } from "@/lib/ai/analysis-target";
import type { AiModelAccess } from "@/lib/ai/model-access";
import { ModelId } from "@/lib/ai/models";

export type ChatOrderKey = "CREATED_AT" | "UPDATED_AT";
export type ChatOrderDirection = "ASC" | "DESC";

/**
 * AI チャット UI 全体で共有したい状態と操作を集約するコンテキスト。
 * フル画面/ドックの両方で同じ API を使えるようにする。
 */
export type AiChatUiContextType = {
  chatId: string | null;
  workspaceId: string;
  lang: string | null;

  // チャット遷移
  chatLinkComponent: FC<ChatLinkComponentProps>;
  selectChat: (chatId: string) => void;
  clearChatSelection: () => void;

  // モデル選択
  currentModel: ModelId;
  selectModel: (modelId: ModelId) => void;
  aiModelAccess: AiModelAccess;

  // 分析対象選択（任意・複数）
  analysisTargets: AnalysisTarget[];
  pageAnalysisTarget: AnalysisTarget | null;
  manualAnalysisTargets: AnalysisTarget[];
  setAnalysisTargets: (targets: AnalysisTarget[]) => void;
  addAnalysisTarget: (target: AnalysisTarget) => void;
  removeAnalysisTarget: (target: AnalysisTarget) => void;
  removeLastAnalysisTarget: () => void;
  clearAnalysisTargets: () => void;
  setPageAnalysisTarget: (target: AnalysisTarget | null) => void;

  // チャット一覧制御
  orderKey: ChatOrderKey;
  orderDirection: ChatOrderDirection;
  includesShared: boolean;
  setOrder: (key: ChatOrderKey, direction: ChatOrderDirection) => void;
  toggleOrderDirection: () => void;
  setIncludesShared: (include: boolean) => void;

  /** When true, tool confirmation UIs auto-submit approve for eligible actions. */
  autoApproveToolConfirmations: boolean;
  setAutoApproveToolConfirmations: (enabled: boolean) => void;
};

/**
 * AI チャット UI 全体で共有したい状態と操作を集約するコンテキスト。
 * フル画面/ドックの両方で同じ API を使えるようにする。
 */
export const AiChatUiContext = createContext<AiChatUiContextType | null>(null);

/**
 * AI チャット UI 全体で共有したい状態と操作を集約するコンテキスト。
 * フル画面/ドックの両方で同じ API を使えるようにする。
 */
export function useAiChatUiContext() {
  const context = useContext(AiChatUiContext);
  if (!context) {
    throw new Error(
      "useAiChatUiContext must be used within an AiChatUiContextProvider",
    );
  }
  return context;
}

/**
 * AI チャット機能で、チャットリストアイテムの内部で使われるLinkコンポーネントに与えるべきProps.
 * コンポーネントの内部でnext/navigationのLinkを使うのか、それともそれ以外の実装にするのかは実装に委ねられる。
 */
export type ChatLinkComponentProps = {
  chatId: string;
  isSelected?: boolean;
  children: ReactNode;
  className?: string;
};
